import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { AdminPage, LoginPage, SettingsPage } from "./auth/AuthPages";
import type { SessionUser } from "./auth/AuthPages";
import { ChoresApp } from "./chores/ChoresApp";
import { CalendarApp } from "./calendar/CalendarApp";
import type { SwitcherUser } from "./components/AppSwitcher";
import { AppContainer } from "./components/AppContainer";
import { DinnerApp } from "./dinner/DinnerApp";
import { GroceriesApp } from "./groceries/GroceriesApp";
import { TodoApp } from "./todo/TodoApp";
import { ActivitiesApp } from "./activities/ActivitiesApp";
import { GamesApp } from "./games/GamesApp";
import { RecipesApp } from "./recipes/RecipesApp";
import "./index.css";
import { APP_NAMES, apps, isAppName } from "./lib/apps";
import type { AppName } from "./lib/apps";
import { selectHomeTitle } from "./lib/homeTitles";
import { applyTheme } from "./theme";

export function HomePage({ user, onSignOut }: { user: SwitcherUser; onSignOut: () => Promise<void> }) {
  // A lazy initializer intentionally keeps the choice stable across rerenders.
  const [title] = useState(selectHomeTitle);
  return (
    <AppContainer className="site-shell" currentDestination="home" user={user} onSignOut={onSignOut} title={title} asHeading>
      <nav aria-label="Family apps" className="app-grid">
        {APP_NAMES.map(slug => {
          const app = apps[slug];
          const Icon = app.Icon;
          return (
            <a className={`app-card app-${slug}`} href={app.launcherHref ?? app.href} key={slug}>
              <span className="app-icon" aria-hidden="true"><Icon size={25} strokeWidth={0.75} /></span>
              <span className="app-card-copy">
                <span className="app-card-title">{app.title}</span>
              </span>
            </a>
          );
        })}
      </nav>
    </AppContainer>
  );
}

function PlaceholderPage({ appName, user, onSignOut }: { appName: AppName; user: SwitcherUser; onSignOut: () => Promise<void> }) {
  const app = apps[appName];
  const Icon = app.Icon;
  return (
    <AppContainer className={`placeholder-shell app-${appName}`} currentDestination={appName} user={user} onSignOut={onSignOut}>
      <section className="placeholder-card">
        <span className="app-icon placeholder-icon" aria-hidden="true"><Icon size={30} strokeWidth={1.7} /></span>
        <p className="eyebrow">Coming soon</p>
        <h1>{app.title}</h1>
        <p>We’re making a thoughtful place for {app.title.toLowerCase()}.</p>
      </section>
    </AppContainer>
  );
}

function ProtectedApp({ user, refreshUser, route }: { user: SessionUser; refreshUser: () => Promise<void>; route: string }) {
  const firstSegment = route.split("/")[1] ?? "";
  const appName = isAppName(firstSegment) ? firstSegment : null;

  useEffect(() => {
    if (user.mustChangePassword && route !== "/settings") window.location.replace("/settings/?required=1");
  }, [route, user.mustChangePassword]);

  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST" });
    window.location.assign("/login");
  }

  const switcherUser: SwitcherUser = { id: user.id, displayName: user.displayName, isAdmin: user.isAdmin };

  if (route === "/settings") return <SettingsPage user={user} refreshUser={refreshUser} />;
  if (route === "/admin") return user.isAdmin ? <AdminPage currentUser={user} /> : <HomePage user={switcherUser} onSignOut={signOut} />;
  if (appName === "chores") return <ChoresApp route={route} appName={appName} user={switcherUser} onSignOut={signOut} />;
  if (appName === "todo") return <TodoApp route={route} user={switcherUser} onSignOut={signOut} />;
  if (appName === "games") return <GamesApp route={route} user={switcherUser} onSignOut={signOut} />;
  if (appName === "activities") return <ActivitiesApp route={route} user={switcherUser} onSignOut={signOut} />;
  if (appName === "dinner") return <DinnerApp route={route} appName={appName} user={switcherUser} onSignOut={signOut} />;
  if (appName === "groceries") return <GroceriesApp route={route} appName={appName} user={switcherUser} onSignOut={signOut} />;
  if (appName === "recipes") return <RecipesApp route={route} user={switcherUser} onSignOut={signOut} />;
  if (appName === "calendar") return <CalendarApp route={route} user={switcherUser} onSignOut={signOut} />;
  return appName ? <PlaceholderPage appName={appName} user={switcherUser} onSignOut={signOut} /> : <HomePage user={switcherUser} onSignOut={signOut} />;
}

export function App() {
  const [route, setRoute] = useState(() => window.location.pathname.replace(/\/+$/, "") || "/");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(route !== "/login");

  useEffect(() => {
    const syncRoute = () => setRoute(window.location.pathname.replace(/\/+$/, "") || "/");
    const openCreationRoute = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!target || target.target || target.hasAttribute("download")) return;
      const url = new URL(target.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/(?:new|import-url)\/?$/.test(url.pathname)) return;
      event.preventDefault();
      window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
      flushSync(syncRoute);
    };
    window.addEventListener("popstate", syncRoute);
    document.addEventListener("click", openCreationRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
      document.removeEventListener("click", openCreationRoute);
    };
  }, []);

  async function refreshUser() {
    const response = await fetch("/api/auth/session", { cache: "no-store" });
    if (!response.ok) { window.location.replace(`/login?next=${encodeURIComponent(window.location.pathname)}`); return; }
    const data = await response.json() as { user: SessionUser };
    applyTheme(data.user.theme);
    setUser(data.user);
  }

  useEffect(() => {
    if (route === "/login") return;
    void refreshUser().finally(() => setLoading(false));
  }, [route]);

  if (route === "/login") return <LoginPage />;
  if (loading || !user) return <main className="auth-shell"><div className="loading-dot" aria-label="Loading" /></main>;
  return <ProtectedApp user={user} refreshUser={refreshUser} route={route} />;
}

export default App;

export { PlaceholderPage };
